import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { documentsList } from "../../store/actions/documentsActions";
import { fetchPriceApparat } from "../../store/actions/paymentsActions";
import BlankPaperPreview from "./BlankPaperPreview";
import FileTypeIcon from "../../components/FileTypeIcon";
import { useKioskToast, KioskToast } from "../../components/useKioskToast";
import {
    SERVICE_SESSION_EVENT,
    getServiceToken,
    isFreePrintEnabled,
} from "../../services/kioskServiceSession";

const MAX_SELECTED = 3;
const FALLBACK_BLANK_PRICE = 30; // пока priceApparat ещё не загрузился
// Памятки в плашке над сеткой сменяют друг друга — второй блок ради
// подсказки про пустые страницы не нужен. Она — только там, где
// предупреждение о пустых страницах включено в админке.
const PILL_NOTES = ["listDoc.pill_note"];
const PILL_NOTES_BLANK = [...PILL_NOTES, "listDoc.pill_blank_pages"];
const PILL_NOTE_MS = 6000;

const blockVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.45, delay: i * 0.1 },
    }),
};

function ListDoc() {
    const dispatch = useDispatch();
    const location = useLocation();
    const { documentsDatas, loading, error } = useSelector(
        (state) => state.documentsList,
    );
    const { priceApparat } = useSelector((state) => state.payments);
    const { t } = useTranslation();

    const [filter, setFilter] = useState("new"); // "new" | "old"
    const [selectedIds, setSelectedIds] = useState([]);
    const [serviceMode, setServiceMode] = useState(Boolean(getServiceToken()));
    const [freePrint, setFreePrint] = useState(isFreePrintEnabled());
    const { toastMsg, showToast } = useKioskToast();
    const [pillNote, setPillNote] = useState(0);
    const pillNotes = priceApparat?.kiosk_settings?.blank_pages_warning === true ? PILL_NOTES_BLANK : PILL_NOTES;

    useEffect(() => {
        if (pillNotes.length < 2) return undefined;
        const timer = setInterval(() => setPillNote((n) => n + 1), PILL_NOTE_MS);
        return () => clearInterval(timer);
    }, [pillNotes.length]);

    useEffect(() => {
        dispatch(documentsList());
        dispatch(fetchPriceApparat());
    }, [dispatch]);

    useEffect(() => {
        const syncServiceState = () => {
            setServiceMode(Boolean(getServiceToken()));
            setFreePrint(isFreePrintEnabled());
        };
        window.addEventListener(SERVICE_SESSION_EVENT, syncServiceState);
        return () => window.removeEventListener(SERVICE_SESSION_EVENT, syncServiceState);
    }, []);

    const handleSelectDoc = (id) => {
        setSelectedIds((prev) => {
            if (prev.includes(id)) {
                return prev.filter((itemId) => itemId !== id);
            }
            if (prev.length >= MAX_SELECTED) {
                showToast(t("listDoc.toast_max"));
                return prev;
            }
            return [...prev, id];
        });
    };

    const sortedDocuments = documentsDatas
        ? [...documentsDatas].sort((a, b) =>
              filter === "new"
                  ? new Date(b.created_at) - new Date(a.created_at)
                  : new Date(a.created_at) - new Date(b.created_at),
          )
        : [];

    const blankDisabled = selectedIds.length > 0;

    return (
        <div className="h-[88vh] px-[2.4vw] py-[2vh] flex flex-col gap-[1.8vh] bg-[#EDEFF4]">
            {/* Заголовок, фильтр по дате, обновление */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={0}
                className="flex-none flex items-center gap-[1.4vw]"
            >
                <h2 className="text-[2.8vh] font-extrabold tracking-tight mr-auto">
                    {t("listDoc.title")}
                </h2>
                {serviceMode && (
                    <Link data-analytics="listdoc.link.1"
                        to="/service"
                        className="flex items-center gap-[0.55vw] rounded-full bg-[#12161F] text-white px-[1.5vw] py-[0.85vh] text-[1.25vh] font-bold"
                    >
                        <span aria-hidden="true">⚙</span>
                        {t(freePrint ? "listDoc.service_free" : "listDoc.service")}
                    </Link>
                )}
                <div className="flex bg-[#F1F3F7] rounded-full p-[0.4vh]">
                    <button data-analytics="listdoc.listDoc.filter_new.2"
                        onClick={() => setFilter("new")}
                        className={`px-[1.8vw] py-[0.8vh] rounded-full text-[1.5vh] font-bold transition-colors ${
                            filter === "new"
                                ? "bg-[#12161F] text-white"
                                : "text-[#7B8290]"
                        }`}
                    >
                        {t("listDoc.filter_new")}
                    </button>
                    <button data-analytics="listdoc.listDoc.filter_old.3"
                        onClick={() => setFilter("old")}
                        className={`px-[1.8vw] py-[0.8vh] rounded-full text-[1.5vh] font-bold transition-colors ${
                            filter === "old"
                                ? "bg-[#12161F] text-white"
                                : "text-[#7B8290]"
                        }`}
                    >
                        {t("listDoc.filter_old")}
                    </button>
                </div>
                <button data-analytics="listdoc.listDoc.refresh.4"
                    onClick={() => dispatch(documentsList())}
                    disabled={loading}
                    className="flex-none flex items-center gap-[0.6vw] bg-[#F1F3F7] text-[#12161F] rounded-full py-[1vh] px-[2vw] text-[1.5vh] font-bold disabled:opacity-60"
                >
                    {t("listDoc.refresh")}
                    <svg
                        className="w-[1.8vh] h-[1.8vh]"
                        style={loading ? { animation: "spin 0.8s linear infinite" } : undefined}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
                    </svg>
                </button>
            </motion.div>

            {/* Памятки: лимит в 3 документа / пустые страницы */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={1}
                className="flex-none bg-[#E3ECFB] text-[#246CE2] rounded-[1.6vh] py-[1.2vh] px-[1.8vw] text-[1.4vh] font-bold flex items-center justify-center gap-[1vw]"
            >
                <svg
                    className="w-[2vh] h-[2vh] flex-none"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v5M12 16.5v.01" />
                </svg>
                <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                        key={pillNote % pillNotes.length}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="min-w-0 truncate"
                    >
                        {t(pillNotes[pillNote % pillNotes.length])}
                    </motion.span>
                </AnimatePresence>
            </motion.div>

            {/* Сетка файлов */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={2}
                className="flex-1 min-h-0 overflow-y-auto grid grid-cols-2 gap-[1.4vh] content-start"
            >
                {loading && sortedDocuments.length === 0 && (
                    <div className="col-span-2 flex flex-col items-center justify-center gap-[1.2vh] py-[4vh]">
                        <svg
                            className="w-[3.6vh] h-[3.6vh]"
                            style={{ animation: "spin 0.8s linear infinite" }}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#246CE2"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                        >
                            <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
                        </svg>
                        <p className="text-[1.6vh] font-semibold text-[#7B8290]">
                            {t("listDoc.loading")}
                        </p>
                    </div>
                )}

                {error && (
                    <div className="col-span-2 text-center text-[1.6vh] font-semibold text-[#C2543E] py-[4vh]">
                        {t("listDoc.error")}:{" "}
                        {typeof error === "object"
                            ? error.detail ||
                              error.message ||
                              t("listDoc.error_default")
                            : error}
                    </div>
                )}

                {!loading &&
                    !error &&
                    (!documentsDatas || documentsDatas.length === 0) && (
                        <div className="col-span-2 text-center py-[4vh]">
                            <p className="text-[1.8vh] font-bold">
                                {t("listDoc.no_documents.message")}
                            </p>
                            <p className="text-[1.4vh] font-semibold text-[#7B8290] mt-[0.8vh]">
                                {t("listDoc.no_documents.send")}
                            </p>
                        </div>
                    )}

                {!error &&
                    sortedDocuments.map((doc) => {
                        const isSelected = selectedIds.includes(doc.id);
                        const extension = doc.file_name
                            .split(".")
                            .pop()
                            .toLowerCase();
                        const displayName = doc.file_name;

                        const createdAt = new Date(doc.created_at);
                        const diffMs = Date.now() - createdAt;
                        const minutes = Math.floor(diffMs / 60000);
                        const hours = Math.floor(minutes / 60);
                        const days = Math.floor(hours / 24);
                        const timeAgo =
                            days > 0
                                ? t("docItems.time_ago.days", { count: days })
                                : hours > 0
                                  ? t("docItems.time_ago.hours", { count: hours })
                                  : minutes > 0
                                    ? t("docItems.time_ago.minutes", {
                                          count: minutes,
                                      })
                                    : t("docItems.time_ago.seconds", {
                                          count: Math.floor(diffMs / 1000),
                                      });

                        const statusIsPrinted = doc.status === "printed";
                        const statusIsError =
                            doc.status === "conversion_failed";
                        const statusLabel = statusIsError
                            ? t("docItems.status.conversion_failed")
                            : statusIsPrinted
                              ? t("docItems.status.printed")
                              : t("docItems.status.ready");

                        return (
                            <button data-analytics="listdoc.handleSelectDoc.5"
                                key={doc.id}
                                type="button"
                                onClick={() => handleSelectDoc(doc.id)}
                                className={`bg-white rounded-[2vh] p-[1.6vh_1.4vw] flex items-center gap-[1.2vw] text-left border-[0.25vh] transition-colors active:bg-[#F8FAFD] ${
                                    isSelected
                                        ? "border-[#246CE2] bg-[#FBFCFE]"
                                        : "border-transparent shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)]"
                                }`}
                            >
                                <FileTypeIcon
                                    extension={extension}
                                    className="flex-none w-[3.4vh] h-[4vh]"
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="text-[1.5vh] font-bold truncate">
                                        {displayName}
                                    </div>
                                    <div className="text-[1.15vh] font-semibold text-[#7B8290] mt-[0.4vh] flex items-center gap-[0.6vw] flex-wrap">
                                        {timeAgo}
                                        <span
                                            className={`rounded-full px-[1vw] py-[0.2vh] ${
                                                statusIsError
                                                    ? "bg-[#F7E1DC] text-[#C2543E]"
                                                    : statusIsPrinted
                                                      ? "bg-[#E3ECFB] text-[#246CE2]"
                                                      : "bg-[#F1F3F7] text-[#7B8290]"
                                            }`}
                                        >
                                            {statusLabel}
                                        </span>
                                    </div>
                                </div>
                                <div
                                    className={`flex-none w-[2.7vh] h-[2.7vh] rounded-full border-[0.2vh] flex items-center justify-center transition-colors ${
                                        isSelected
                                            ? "bg-[#246CE2] border-[#246CE2]"
                                            : "border-[#E1E4EB]"
                                    }`}
                                >
                                    <AnimatePresence>
                                        {isSelected && (
                                            <motion.svg
                                                initial={{ scale: 0, opacity: 0 }}
                                                animate={{ scale: 1, opacity: 1 }}
                                                exit={{ scale: 0, opacity: 0 }}
                                                transition={{ duration: 0.15 }}
                                                className="w-[1.3vh] h-[1.3vh]"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="#fff"
                                                strokeWidth="3"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M4 12.5l5 5L20 6.5" />
                                            </motion.svg>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </button>
                        );
                    })}
            </motion.div>

            <motion.section variants={blockVariants} initial="hidden" animate="visible" custom={3} aria-label={t("listDoc.blank_name")} className="flex-none bg-white shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] rounded-[2.2vh] px-[2vw] py-[1.5vh] flex items-center gap-[1.6vw]">
                <BlankPaperPreview className="flex-none w-[3.4vh] h-[4vh]" />
                <div className="min-w-0 flex-1">
                    <p className="inline-block rounded-full bg-[#E3ECFB] px-[1vw] py-[0.25vh] text-[1.1vh] font-bold text-[#246CE2]">{t("listDoc.blank_chip")}</p>
                    <h3 className="mt-[0.5vh] text-[1.5vh] font-bold text-[#12161F]">{t("listDoc.blank_name")}</h3>
                    <p className="text-[1.15vh] font-semibold text-[#7B8290] mt-[0.3vh]">{t("listDoc.blank_description")}</p>
                    <p className="text-[1.15vh] font-semibold text-[#7B8290] mt-[0.4vh]">
                        {blankDisabled ? t("listDoc.blank_disabled") : t("listDoc.blank_price", { price: freePrint ? 0 : priceApparat?.price_for_blank_sheet ?? FALLBACK_BLANK_PRICE })}
                    </p>
                </div>
                <Link data-analytics="listdoc.listDoc.blank_action.6" to={blankDisabled ? "#" : "/printing/blank_sheet"}
                    state={{ from: "/printing/show_list/user" }}
                    onClick={(event) => { if (blankDisabled) event.preventDefault(); }}
                    aria-disabled={blankDisabled} tabIndex={blankDisabled ? -1 : 0}
                    className={`flex-none max-w-[25vw] rounded-full px-[2vw] py-[1.5vh] text-[1.5vh] text-center font-bold bg-[#246CE2] text-white transition-colors ${blankDisabled ? "opacity-40" : "active:bg-[#1B55B4]"}`}>
                    {t("listDoc.blank_action")}
                </Link>
            </motion.section>

            {/* Счётчик выбранных и переход к настройкам печати */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={4}
                className="flex-none relative bg-[#12161F] text-white rounded-[2.2vh] py-[1.4vh] px-[2.4vw] flex items-center gap-[1.6vw]"
            >
                <KioskToast message={toastMsg} position="bottom" />

                <span className="text-[1.7vh] font-bold">
                    {t("listDoc.selected_of", { count: selectedIds.length })}
                </span>

                <Link data-analytics="listdoc.listDoc.continue.7"
                    to={{
                        pathname: "/printing/edit_many_docs",
                        search: `?ids_docs=${selectedIds.join(",")}`,
                    }}
                    state={{ from: location.pathname + location.search }}
                    onClick={(e) => {
                        if (selectedIds.length === 0) e.preventDefault();
                    }}
                    className={`ml-auto flex items-center gap-[0.8vw] bg-[#246CE2] rounded-full py-[1.2vh] px-[2.2vw] text-[1.7vh] font-bold transition-opacity ${
                        selectedIds.length === 0
                            ? "opacity-40 pointer-events-none"
                            : "opacity-100"
                    }`}
                >
                    {t("listDoc.continue")}
                    <svg
                        className="w-[2vh] h-[2vh]"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                </Link>
            </motion.div>
        </div>
    );
}

export default ListDoc;
